/**
 * S3 presigned POST Policy（DC-7650）でのアップロード共通ヘルパー。
 *
 * backend が `content-length-range` でサイズ上限をサーバーサイド強制し、署名済みの
 * `fields`（key / Content-Type / x-amz-meta-* / tagging / policy / signature 等）を返す。
 * FE は fields を素通しで multipart/form-data に詰め、`file` を最後に置いて POST するだけでよい。
 */

import i18next from 'i18next';
import { defaultNS } from '@/lib/i18n';

/** presigned POST 発行 API の戻り値（FE が S3 へ POST する際に必要な部分）。 */
export type PresignedPostTarget = {
  url: string;
  fields: Record<string, string>;
};

/**
 * BE `S3UploadSizeLimit` に対応する用途別上限（MB）。
 * EntityTooLarge 時の i18n 表示に使用する。
 */
export const PRESIGNED_POST_SIZE_LIMIT_MB = {
  AVATAR_LOGO: 10,
  CSV: 10,
  /** BE `S3UploadSizeLimit.DEVICE_FILE` と同期必須。100MB は暫定値（DC-8388）。 */
  DEVICE_FILE: 100,
  // DEVICE_FILE: 10, // DC-8388 で一時検討。ビジネス根拠不足のため見送り（2026-07）
  DATA_PIPELINE_CSV: 100
} as const;

/** {@link PRESIGNED_POST_SIZE_LIMIT_MB} 未指定時のフォールバック（DATA_PIPELINE_CSV 等）。 */
export const PRESIGNED_POST_MAX_FILE_SIZE_MB = PRESIGNED_POST_SIZE_LIMIT_MB.DATA_PIPELINE_CSV;
const BYTES_TO_MB = 1024 * 1024;
const SIZE_DISPLAY_DECIMALS = 2;
export type UploadViaPresignedPostOptions = {
  /** BE S3UploadSizeLimit に対応する上限（MB）。 */
  maxFileSizeMb?: number;
};

/** presigned POST の失敗（非 2xx）。S3 は 4xx で XML エラー（EntityTooLarge 等）を返す。 */
export class PresignedPostUploadError extends Error {
  constructor(readonly status: number, /** S3 XML の `<Code>`。取得できなかった場合は undefined。例: 'EntityTooLarge' */
  readonly s3Code: string | undefined, readonly fileSizeBytes?: number, readonly maxFileSizeMb?: number) {
    super(`PresignedPostUploadError: ${s3Code ?? 'unknown'} (HTTP ${status})`);
    this.name = 'PresignedPostUploadError';
  }
}

/**
 * {@link PresignedPostUploadError} のユーザー向けメッセージを i18n 解決する。
 * handleApiError および各 hook の onError から利用する。
 */
export function getPresignedPostUploadErrorMessage(error: PresignedPostUploadError, i18n: {
  language: string;
}): string {
  const tOptions = {
    lng: i18n.language,
    ns: defaultNS
  };
  if (error.s3Code === 'EntityTooLarge') {
    const maxSize = (error.maxFileSizeMb ?? PRESIGNED_POST_MAX_FILE_SIZE_MB).toString();
    const actualSize = error.fileSizeBytes !== undefined ? (error.fileSizeBytes / BYTES_TO_MB).toFixed(SIZE_DISPLAY_DECIMALS) : '-';
    return i18next.t($ => $.components.upload.error.fileSizeExceeded, {
      ...tOptions,
      maxSize,
      actualSize
    });
  }
  return i18next.t($ => $.components.upload.error.uploadFailed, tOptions);
}
const parseS3ErrorCode = async (res: Response): Promise<string | undefined> => {
  try {
    const text = await res.text();
    return /<Code>([^<]+)<\/Code>/.exec(text)?.[1];
  } catch {
    return undefined;
  }
};

/**
 * presigned POST Policy で発行された `url` / `fields` を使い S3 へ multipart アップロードする。
 *
 * - `fields` を全件 append したのち `file` を **最後** に append する（S3 仕様: file 以降のフィールドは無視される）。
 * - リクエストに `Content-Type` ヘッダーは付けない（FormData が multipart boundary を自動設定する）。
 *   オブジェクトの Content-Type は `fields['Content-Type']`（backend 署名済み）で決まる。
 * - 成功は 204（backend は `success_action_status` 未設定）。非 2xx は {@link PresignedPostUploadError} を throw する。
 *
 * @param filename FormData の file パートに付与する名前。Blob でも名前を欠落させないため既定 'file'。
 */
export async function uploadViaPresignedPost(url: string, fields: Record<string, string>, file: Blob, filename = 'file', options?: UploadViaPresignedPostOptions): Promise<void> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    form.append(key, value);
  }
  form.append('file', file, filename);
  const res = await fetch(url, {
    method: 'POST',
    body: form
  });
  if (!res.ok) {
    const s3Code = await parseS3ErrorCode(res);
    throw new PresignedPostUploadError(res.status, s3Code, file.size, options?.maxFileSizeMb);
  }
}